import { useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { useApp } from './store.jsx'
import { useSession } from './lib/auth-client'
import { Toasts, Modal, ScrollTop, NotificationsDrawer } from './components/Common.jsx'
import { Sidebar, Topbar } from './components/AppShell.jsx'
import { useRole } from './hooks/useRole'
import { TrialBanner } from './components/TrialBanner.jsx'
import Landing from './pages/Landing.jsx'
import SigninPage from './pages/SigninPage.jsx'
import SignupPage from './pages/SignupPage.jsx'
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx'
import ResetPasswordPage from './pages/ResetPasswordPage.jsx'
import AcceptInvitePage from './pages/AcceptInvitePage.jsx'
import CompleteSignupPage from './pages/CompleteSignupPage.jsx'
import TwoFactorPage from './pages/TwoFactorPage.jsx'
import Dashboard from './pages/app/Dashboard.jsx'
import Reports from './pages/app/Reports.jsx'
import Employees from './pages/app/Employees.jsx'
import Managers from './pages/app/Managers.jsx'
import {
  Departments, Analytics, WhatsApp, Reminders, Billing, Settings,
} from './pages/app/OtherPages.jsx'
import AssignTasks from './pages/app/AssignTasks.jsx'
import MyTasks from './pages/app/MyTasks.jsx'
import ChatBot from './components/ChatBot'
import NotificationsPoller from './components/NotificationsPoller.jsx'
import AdminOverview from './pages/admin/AdminOverview.jsx'
import AdminCompanies from './pages/admin/AdminCompanies.jsx'
import AdminCompanyDetail from './pages/admin/AdminCompanyDetail.jsx'
import AdminSubscriptions from './pages/admin/AdminSubscriptions.jsx'
import AdminBilling from './pages/admin/AdminBilling.jsx'
import AdminPlans from './pages/admin/AdminPlans.jsx'
import AdminSystem from './pages/admin/AdminSystem.jsx'
import AdminAuditLog from './pages/admin/AdminAuditLog.jsx'
import AdminAdmins from './pages/admin/AdminAdmins.jsx'
import AdminSupport from './pages/admin/AdminSupport.jsx'
import RequireRole from './components/admin/RequireRole.jsx'
import HolidaysPage from './pages/app/HolidaysPage.jsx'
import MyCalendar from './pages/app/MyCalendar.jsx'
import Projects from './pages/app/Projects.jsx'
import ProjectDetail from './pages/app/ProjectDetail.jsx'
import PerformanceReviews from './pages/app/PerformanceReviews.jsx'
import Announcements from './pages/app/Announcements.jsx'
import ActivityLog from './pages/app/ActivityLog.jsx'
import LatestUpdates from './pages/app/LatestUpdates.jsx'
import AdminChangelog from './pages/admin/AdminChangelog.jsx'
import AdminCoupons from './pages/admin/AdminCoupons.jsx'
import AdminIpBlocks from './pages/admin/AdminIpBlocks.jsx'
import MaintenanceGate from './components/MaintenanceGate.jsx'
import ImpersonationBanner from './components/ImpersonationBanner.jsx'

const PAGE_COMPONENTS = {
  dashboard: Dashboard,
  reports: Reports,
  managers: Managers,
  employees: Employees,
  departments: Departments,
  analytics: Analytics,
  whatsapp: WhatsApp,
  reminders: Reminders,
  billing: Billing,
  settings: Settings,
  assignTasks: AssignTasks,
  myTasks: MyTasks,
  holidays: HolidaysPage,
  myCalendar: MyCalendar,
  projects: Projects,
  projectDetail: ProjectDetail,
  performance: PerformanceReviews,
  announcements: Announcements,
  activityLog: ActivityLog,
  latestUpdates: LatestUpdates,
  adminOverview: AdminOverview,
  adminCompanies: AdminCompanies,
  adminCompanyDetail: AdminCompanyDetail,
  adminSubscriptions: AdminSubscriptions,
  adminBilling: AdminBilling,
  adminPlans: AdminPlans,
  adminSystem: AdminSystem,
  adminAuditLog: AdminAuditLog,
  adminAdmins: AdminAdmins,
  adminSupport: AdminSupport,
  adminChangelog: AdminChangelog,
  adminCoupons: AdminCoupons,
  adminIpBlocks: AdminIpBlocks,
}

const ADMIN_PAGES = new Set([
  'adminOverview', 'adminCompanies', 'adminCompanyDetail',
  'adminSubscriptions', 'adminBilling', 'adminPlans',
  'adminSystem', 'adminAuditLog', 'adminAdmins', 'adminSupport',
  'adminChangelog', 'adminCoupons', 'adminIpBlocks',
])

// Guards routes that require a full account (companyId set). Incomplete
// Google OAuth users (no companyId yet) are redirected to /complete-signup
// to finish setting up their workspace.
function ProtectedRoute({ children }) {
  const { data, isPending } = useSession()
  if (isPending) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    )
  }
  if (!data?.session) return <Navigate to="/signin" replace />
  // Google OAuth users land with companyId=null — force them to complete
  // their profile before accessing the app.
  if (!data.user.companyId) return <Navigate to="/complete-signup" replace />
  return children
}

// Lighter guard that only requires an active session (even without a
// companyId). Used for the /complete-signup page itself, otherwise we'd
// create a redirect loop (ProtectedRoute → /complete-signup → ProtectedRoute).
function RequireSession({ children }) {
  const { data, isPending } = useSession()
  if (isPending) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    )
  }
  if (!data?.session) return <Navigate to="/signin" replace />
  return children
}

function AppShellPage() {
  const { page, goPage, closeModal, setNotifOpen } = useApp()
  const [searchParams, setSearchParams] = useSearchParams()
  const role = useRole()
  const Page = PAGE_COMPONENTS[page] || Dashboard
  const urlPage = searchParams.get('page')
  const firstUrlSync = useRef(true)
  const firstPageSync = useRef(true)

  // Keep the active tab in sync with the browser URL so the back/forward
  // buttons move between tabs instead of jumping straight to the landing page.
  // Each tab switch becomes its own history entry (?page=<tab>).

  // One-time reconciliation on mount: a deep link / restored URL wins; otherwise
  // stamp the current tab into the URL *without* adding a history entry.
  useEffect(() => {
    if (urlPage && urlPage !== page) goPage(urlPage)
    else if (!urlPage) setSearchParams({ page }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // URL → state: back/forward changes the query param, so follow it. The first
  // run (mount) is handled by the reconciliation effect above, so skip it.
  useEffect(() => {
    if (firstUrlSync.current) { firstUrlSync.current = false; return }
    if (urlPage && urlPage !== page) goPage(urlPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage])

  // State → URL: a tab click changes `page`; push a new history entry for it.
  // Skip the mount run so we don't overwrite a deep link before it syncs in.
  useEffect(() => {
    if (firstPageSync.current) { firstPageSync.current = false; return }
    if (page !== urlPage) setSearchParams({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        document.querySelector('.top-search input')?.focus()
      }
      if (e.key === 'Escape') { closeModal(); setNotifOpen(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeModal, setNotifOpen])

  // A 402 from the API (expired subscription / ended trial) means the company
  // is blocked. Only the OWNER can fix it, so bounce just the owner to Billing
  // (the red banner there explains why). Managers/employees can't access the
  // owner-only Billing page — redirecting them would loop against the page
  // allow-list — so they simply see the red banner + non-loading pages.
  useEffect(() => {
    if (role !== 'owner') return
    const onBlocked = () => goPage('billing')
    window.addEventListener('subscription-blocked', onBlocked)
    return () => window.removeEventListener('subscription-blocked', onBlocked)
  }, [role, goPage])

  const isAdminPage = ADMIN_PAGES.has(page)
  const pageNode = isAdminPage ? (
    <RequireRole role="admin"><Page /></RequireRole>
  ) : (
    <Page />
  )

  return (
    <MaintenanceGate>
      <ImpersonationBanner />
      <div className="app-shell">
        <Sidebar />
        <div className="app-main-col">
          <Topbar />
          <TrialBanner />
          <main className="main">{pageNode}</main>
        </div>
        {role !== 'admin' && <ChatBot mode="app" />}
        {role !== 'admin' && <NotificationsPoller />}
      </div>
    </MaintenanceGate>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/signin" element={<SigninPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/two-factor" element={<TwoFactorPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route
          path="/complete-signup"
          element={
            <RequireSession>
              <CompleteSignupPage />
            </RequireSession>
          }
        />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <AppShellPage />
            </ProtectedRoute>
          }
        />
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toasts />
      <Modal />
      <NotificationsDrawer />
      <ScrollTop />
    </BrowserRouter>
  )
}
